// src/components/weather/WeatherForecast.js

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  FaWind,
  FaTint,
  FaSpinner,
  FaCloudSunRain,
  FaExclamationTriangle,
  FaChevronUp,
  FaClock,
} from 'react-icons/fa';
import { getWeatherData, getHourlyForecast } from '../../services/weatherService';
import GooglePlaceAutocomplete from '../shared/GooglePlaceAutocomplete';
import WeatherIcon from './WeatherIcon';
import {
  getConditionAccent,
  getDayName,
  formatDate,
  roundTemp,
} from './weatherUtils';

const DEFAULT_LOCATION = {
  lat: 42.52449315,
  lng: -71.77227986,
  name: 'Leominster, MA',
};

/* ---------- STYLED COMPONENTS ---------- */

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(p) => p.theme.spacing(2)};
  gap: ${(p) => p.theme.spacing(2)};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing(1)};

  h3 {
    margin: 0;
    color: ${(p) => p.theme.colors.primary};
    font-size: ${(p) => p.theme.typography.fontSize.large};
    display: flex;
    align-items: center;
    gap: ${(p) => p.theme.spacing(1)};
  }
`;

const LocationName = styled.span`
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: ${(p) => p.theme.typography.fontSize.small};
  font-weight: ${(p) => p.theme.typography.fontWeight.regular};
  margin-left: ${(p) => p.theme.spacing(1)};
`;

const SearchWrapper = styled.div`
  max-width: 320px;
  min-width: 0;

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
  }

  input {
    padding: 10px 10px 10px 36px !important;
    font-size: 13px !important;
    margin: 0 !important;
  }

  svg:first-child {
    left: 10px !important;
    font-size: 0.9rem !important;
  }

  label {
    left: 36px !important;
    font-size: 13px !important;
  }

  > div {
    margin-bottom: 0 !important;
  }
`;

const AlertBanner = styled.div`
  background: linear-gradient(135deg, #F69620, #e65100);
  color: #fff;
  border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(1.5)} ${(p) => p.theme.spacing(2)};
  margin-bottom: ${(p) => p.theme.spacing(2)};
  display: flex;
  align-items: flex-start;
  gap: ${(p) => p.theme.spacing(1)};
  font-size: ${(p) => p.theme.typography.fontSize.small};

  svg { margin-top: 2px; flex-shrink: 0; }

  .alert-text {
    .event { font-weight: ${(p) => p.theme.typography.fontWeight.bold}; }
  }
`;

const ForecastGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${(p) => p.theme.spacing(1.5)};

  @media (max-width: 1100px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    gap: ${(p) => p.theme.spacing(1)};
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;
    margin: 0 -${(p) => p.theme.spacing(1)};
    padding-left: ${(p) => p.theme.spacing(1)};
    padding-right: ${(p) => p.theme.spacing(1)};

    &::-webkit-scrollbar { height: 3px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${(p) => p.theme.colors.inputGray}44;
      border-radius: 2px;
    }
  }
`;

const DayCard = styled.div`
  background: ${(p) => p.theme.colors.background};
  border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(2)} ${(p) => p.theme.spacing(1.5)};
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid transparent;
  scroll-snap-align: start;
  cursor: pointer;

  @media (max-width: 768px) {
    min-width: 115px;
    max-width: 130px;
    flex-shrink: 0;
    padding: ${(p) => p.theme.spacing(1.5)} ${(p) => p.theme.spacing(1)};
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    border-color: ${(p) => p.accent || p.theme.colors.primary}44;
  }

  ${(p) => p.$isToday && `
    border-color: ${p.theme.colors.primary}66;
    background: ${p.theme.colors.surface};
  `}

  ${(p) => p.$isSelected && `
    border-color: ${p.theme.colors.primary};
    box-shadow: 0 0 0 1px ${p.theme.colors.primary}66;
  `}
`;

const DayName = styled.div`
  font-weight: ${(p) => p.theme.typography.fontWeight.bold};
  color: ${(p) => (p.$isToday ? p.theme.colors.primary : p.theme.colors.textPrimary)};
  font-size: 0.85rem;
`;

const DayDate = styled.div`
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 0.7rem;
  margin-bottom: 4px;
`;

const DayIconWrap = styled.div`
  display: flex;
  justify-content: center;
  margin: 4px auto;
`;

const TempRange = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 6px;
  margin: 4px 0;
`;

const HighTemp = styled.span`
  font-weight: ${(p) => p.theme.typography.fontWeight.bold};
  color: ${(p) => p.theme.colors.textPrimary};
  font-size: 1rem;
`;

const LowTemp = styled.span`
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 0.85rem;
`;

const DayNightRow = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 6px;
  font-size: 0.7rem;
  color: ${(p) => p.theme.colors.textSecondary};

  .label {
    text-transform: uppercase;
    font-size: 0.6rem;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }

  .value {
    color: ${(p) => p.theme.colors.textPrimary};
    font-weight: ${(p) => p.theme.typography.fontWeight.medium};
  }
`;

const RainRow = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 6px;
  font-size: 0.65rem;
  gap: 4px;
`;

const RainHalf = styled.div`
  text-align: center;
  flex: 1;

  .rain-label {
    text-transform: uppercase;
    font-size: 0.55rem;
    letter-spacing: 0.3px;
    color: ${(p) => p.theme.colors.textSecondary};
    margin-bottom: 2px;
  }

  .rain-value {
    color: ${(p) => p.$active ? '#42a5f5' : p.theme.colors.textSecondary};
    font-weight: ${(p) => p.$active ? '600' : '400'};
    font-size: 0.7rem;
  }

  .rain-bar {
    width: 100%;
    height: 3px;
    background: ${(p) => p.theme.colors.surface};
    border-radius: 2px;
    margin-top: 2px;
    overflow: hidden;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: ${(p) => p.$percent}%;
      background: ${(p) => (p.$percent > 50 ? '#1976d2' : '#64b5f6')};
      border-radius: 2px;
      transition: width 0.4s ease;
    }
  }
`;

const DayDetailRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 0.65rem;
  color: ${(p) => p.theme.colors.textSecondary};
  svg { font-size: 0.55rem; }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: ${(p) => p.theme.colors.primary};

  svg {
    font-size: 2rem;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  color: ${(p) => p.theme.colors.error};
  text-align: center;
  padding: ${(p) => p.theme.spacing(2)};
`;

/* Hourly detail panel */
const HourlyPanel = styled.div`
  background: ${(p) => p.theme.colors.background};
  border: 1px solid ${(p) => p.theme.colors.primary}44;
  border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(2)};
  margin-top: ${(p) => p.theme.spacing(2)};
  margin-bottom: ${(p) => p.theme.spacing(1)};
`;

const HourlyTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${(p) => p.theme.spacing(1)};
  margin-bottom: ${(p) => p.theme.spacing(1.5)};
  font-size: 0.85rem;
  font-weight: ${(p) => p.theme.typography.fontWeight.bold};
  color: ${(p) => p.theme.colors.primary};
`;

const HourlyGrid = styled.div`
  display: flex;
  gap: ${(p) => p.theme.spacing(1)};
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar { height: 3px; }
  &::-webkit-scrollbar-thumb {
    background: ${(p) => p.theme.colors.inputGray}44;
    border-radius: 2px;
  }
`;

const HourCard = styled.div`
  flex-shrink: 0;
  min-width: 64px;
  text-align: center;
  padding: ${(p) => p.theme.spacing(1)};
  border-radius: ${(p) => p.theme.borderRadius};
  background: ${(p) => p.theme.colors.surface};
  font-size: 0.7rem;

  .time {
    font-weight: ${(p) => p.theme.typography.fontWeight.bold};
    color: ${(p) => p.theme.colors.textPrimary};
    margin-bottom: 2px;
  }

  .temp {
    color: ${(p) => p.theme.colors.textPrimary};
    font-weight: ${(p) => p.theme.typography.fontWeight.medium};
    font-size: 0.8rem;
  }

  .rain {
    color: #64b5f6;
    font-size: 0.65rem;
    margin-top: 2px;
  }

  .desc {
    color: ${(p) => p.theme.colors.textSecondary};
    font-size: 0.6rem;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 70px;
  }
`;

const HourIconWrap = styled.div`
  display: flex;
  justify-content: center;
  margin: 2px auto;
`;

const DetailedText = styled.div`
  font-size: 0.8rem;
  color: ${(p) => p.theme.colors.textSecondary};
  margin-top: ${(p) => p.theme.spacing(1.5)};
  line-height: 1.4;
  padding: ${(p) => p.theme.spacing(1)};
  background: ${(p) => p.theme.colors.surface};
  border-radius: ${(p) => p.theme.borderRadius};
`;

/* ---------- COMPONENT ---------- */

const WeatherForecast = ({ latitude, longitude, title, showSearch = true }) => {
  const isFixed = latitude && longitude && !isNaN(latitude) && !isNaN(longitude);
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(
    isFixed
      ? { lat: parseFloat(latitude), lng: parseFloat(longitude), name: title || 'Job Site' }
      : DEFAULT_LOCATION
  );
  const [selectedDay, setSelectedDay] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [hourlyLoading, setHourlyLoading] = useState(false);

  useEffect(() => {
    if (isFixed) {
      setLocation({ lat: parseFloat(latitude), lng: parseFloat(longitude), name: title || 'Job Site' });
    }
  }, [latitude, longitude, title, isFixed]);

  useEffect(() => {
    let cancelled = false;

    const fetchWeather = async () => {
      setLoading(true);
      setError(null);
      setSelectedDay(null);
      setHourlyData(null);
      try {
        const data = await getWeatherData(location.lat, location.lng);
        if (!cancelled) setWeatherData(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchWeather();
    return () => { cancelled = true; };
  }, [location.lat, location.lng]);

  const handlePlaceSelect = (address, lat, lng) => {
    setLocation({ lat, lng, name: address });
  };

  const handleDayClick = async (day, index) => {
    if (selectedDay === index) {
      setSelectedDay(null);
      return;
    }

    setSelectedDay(index);

    // Load hourly data if not already loaded
    if (!hourlyData) {
      setHourlyLoading(true);
      try {
        const data = await getHourlyForecast(location.lat, location.lng);
        setHourlyData(data);
      } catch {
        // Hourly data is optional — fail silently
      }
      setHourlyLoading(false);
    }
  };

  const getHoursForDay = (day) => {
    if (!hourlyData || !day) return [];
    const dayDate = new Date(day.startTime);
    dayDate.setHours(0, 0, 0, 0);
    const nextDay = new Date(dayDate);
    nextDay.setDate(nextDay.getDate() + 1);

    return hourlyData.filter((h) => {
      const hDate = new Date(h.time);
      return hDate >= dayDate && hDate < nextDay;
    });
  };

  const daily = weatherData?.daily || [];
  const alerts = weatherData?.alerts || [];
  const firstDay = daily[0];
  const accent = firstDay ? getConditionAccent(firstDay.conditionCode) : undefined;

  return (
    <Container>
      <Header>
        <TitleRow>
          <h3>
            <FaCloudSunRain />
            {title || 'Weather Forecast'}
          </h3>
          <LocationName>{location.name}</LocationName>
        </TitleRow>
        {showSearch && !isFixed && (
          <SearchWrapper>
            <GooglePlaceAutocomplete
              onSelect={handlePlaceSelect}
              placeholder="Search location..."
            />
          </SearchWrapper>
        )}
      </Header>

      {loading ? (
        <LoadingContainer>
          <FaSpinner />
        </LoadingContainer>
      ) : error ? (
        <ErrorContainer>{error}</ErrorContainer>
      ) : (
        <>
          {/* Weather Alerts */}
          {alerts.map((alert, i) => (
            <AlertBanner key={i}>
              <FaExclamationTriangle />
              <div className="alert-text">
                <span className="event">{alert.event}</span>
                {alert.description && ` — ${alert.description.substring(0, 200)}${alert.description.length > 200 ? '...' : ''}`}
              </div>
            </AlertBanner>
          ))}

          {/* 7-Day Forecast */}
          <ForecastGrid>
            {daily.slice(0, 7).map((day, i) => {
              const dayName = getDayName(day.dt);
              const isToday = dayName === 'Today';
              const dayPop = day.rain.dayPop || 0;
              const nightPop = day.rain.nightPop || 0;
              const hasRain = dayPop > 0 || nightPop > 0;

              return (
                <DayCard
                  key={day.dt}
                  $isToday={isToday}
                  $isSelected={selectedDay === i}
                  accent={accent}
                  onClick={() => handleDayClick(day, i)}
                >
                  <DayName $isToday={isToday}>{dayName}</DayName>
                  <DayDate>{formatDate(day.dt)}</DayDate>
                  <DayIconWrap>
                    <WeatherIcon iconUrl={day.icon} size={48} />
                  </DayIconWrap>
                  <TempRange>
                    <HighTemp>{roundTemp(day.temp.max)}°</HighTemp>
                    <LowTemp>{roundTemp(day.temp.min)}°</LowTemp>
                  </TempRange>
                  <DayNightRow>
                    <div>
                      <div className="label">Day</div>
                      <div className="value">{roundTemp(day.temp.day)}°</div>
                    </div>
                    <div>
                      <div className="label">Night</div>
                      <div className="value">{roundTemp(day.temp.night)}°</div>
                    </div>
                  </DayNightRow>
                  {hasRain && (
                    <RainRow>
                      <RainHalf $percent={dayPop} $active={dayPop >= 30}>
                        <div className="rain-label"><FaTint /> Day</div>
                        <div className="rain-value">{dayPop}%</div>
                        <div className="rain-bar" />
                      </RainHalf>
                      <RainHalf $percent={nightPop} $active={nightPop >= 30}>
                        <div className="rain-label"><FaTint /> Night</div>
                        <div className="rain-value">{nightPop}%</div>
                        <div className="rain-bar" />
                      </RainHalf>
                    </RainRow>
                  )}
                  <DayDetailRow>
                    <span><FaWind /> {day.wind.speed} mph {day.wind.direction}</span>
                  </DayDetailRow>
                </DayCard>
              );
            })}
          </ForecastGrid>

          {/* Hourly Detail Panel */}
          {selectedDay !== null && daily[selectedDay] && (
            <HourlyPanel>
              <HourlyTitle>
                <span>
                  <FaClock /> {getDayName(daily[selectedDay].dt)} — {daily[selectedDay].shortForecast}
                </span>
                <FaChevronUp
                  style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                  onClick={() => setSelectedDay(null)}
                />
              </HourlyTitle>

              {/* Detailed forecast text */}
              {daily[selectedDay].detailedForecast && (
                <DetailedText>{daily[selectedDay].detailedForecast}</DetailedText>
              )}

              {/* Hourly breakdown */}
              {hourlyLoading ? (
                <LoadingContainer style={{ minHeight: '80px' }}>
                  <FaSpinner style={{ fontSize: '1.2rem' }} />
                </LoadingContainer>
              ) : (
                (() => {
                  const hours = getHoursForDay(daily[selectedDay]);
                  if (hours.length === 0) return null;

                  return (
                    <HourlyGrid>
                      {hours.map((h, idx) => {
                        const time = new Date(h.time);
                        const hour = time.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });

                        return (
                          <HourCard key={idx}>
                            <div className="time">{hour}</div>
                            <HourIconWrap><WeatherIcon iconUrl={h.icon} size={32} /></HourIconWrap>
                            <div className="temp">{h.temp}°</div>
                            {h.rain > 0 && <div className="rain"><FaTint /> {h.rain}%</div>}
                            <div className="desc">{h.shortForecast}</div>
                          </HourCard>
                        );
                      })}
                    </HourlyGrid>
                  );
                })()
              )}
            </HourlyPanel>
          )}
        </>
      )}
    </Container>
  );
};

export default WeatherForecast;
